import React, {useCallback, useEffect, useRef, useState} from 'react';
import {digitFormat, moneyFormat, moneyFormat4decimals} from '../utils';
import './USDIStats.css';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSyncAlt} from '@fortawesome/free-solid-svg-icons';

const HolderInfoTableSolana = ({
                                   selectedChain,
                                   setAmount,
                                   setToAddress,
                                   setTransactionTrigger,
                                   usdiBalance,
                                   isTransferComplete,
                                   setIsTransferComplete
                               }) => {
    // ------------------ 原有 State ------------------
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [updatingDWS, setUpdatingDWS] = useState(false);
    const [calculating, setCalculating] = useState(false);
    const [error, setError] = useState(null);
    const [historicalData, setHistoricalData] = useState({});
    const [hoveredAddress, setHoveredAddress] = useState(null);

    //  States for interest distribution:
    const [finalDistributionAmountAfterManagementFees, setFinalDistributionAmountAfterManagementFees] = useState(null);
    const [usdiInterestDistributionWalletPublicKey, setUsdiInterestDistributionWalletPublicKey] = useState('');
    const [interestDistributing, setInterestDistributing] = useState(false);

    // States for Calculate modal (auto-populate from Overall USDI Payout)
    const [showCalculateModal, setShowCalculateModal] = useState(false);
    const [calculateBlockNumber, setCalculateBlockNumber] = useState('');
    const [calculateFinalAmount, setCalculateFinalAmount] = useState('');
    const [calculateDistributionId, setCalculateDistributionId] = useState('');
    const [latestDistributionIdForDisplay, setLatestDistributionIdForDisplay] = useState(''); // actual latest from fetch, not user input
    const [payoutListForCalculate, setPayoutListForCalculate] = useState([]);

    // State for undistributed value confirmation modal (replaces system alert)
    const [showUndistributedConfirmModal, setShowUndistributedConfirmModal] = useState(false);

    // ------------------ Endpoint  ------------------
    const USDI_API = 'https://usdi-api-jlukzyz7wa-ew.a.run.app';
    const Distribution_USDI = 'https://distribution-calculator-256611876551.europe-west1.run.app';

    const holdersInfoUrl = `${USDI_API}/usdi_holders_info_table_solana`;
    const rawBlockchainTxUrl = `${USDI_API}/raw_blockchain_transactions_solana`;
    const getUserBalanceEndpoint = `${Distribution_USDI}/get_user_balance_solana`;
    const adminTriggerEndpoint = `${Distribution_USDI}/admin-trigger_solana`;
    const userBalanceBlocksUpdateEndpoint = `${Distribution_USDI}/user_balance_blocks_update_solana`;
    const calculationTriggerEndpoint = `${Distribution_USDI}/calculation-trigger_solana`;
    const interestDistributeVerifyStatusUrl = `${Distribution_USDI}/interest_distribute_verify_status`;
    const overallUsdiPayoutSolanaUrl = 'https://usdi-api-256611876551.europe-west1.run.app/overall_usdi_payout_solana';
    const usdiInterestDistributionWalletPublicKeyUrl =
        'https://usdi-api-256611876551.europe-west1.run.app/usdi_interest_distribution_wallet_public_key';

    const [updateMessage, setUpdateMessage] = useState("")
    // ------------------ 原有函数：fetchData ------------------
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(holdersInfoUrl);
            if (!response.ok) throw new Error('Network response was not ok');
            const result = await response.json();
            setData(result);

            const rawResponse = await fetch(rawBlockchainTxUrl);
            if (!rawResponse.ok) throw new Error('Network response was not ok');
            const rawResult = await rawResponse.json();

            // Build a map of address => numeric userId
            const addressMap = {};
            let userId = 1;
            rawResult.forEach((transaction) => {
                const normalizedAddress = transaction.to_address.toLowerCase().slice(0, 42);
                if (!addressMap[normalizedAddress]) {
                    addressMap[normalizedAddress] = userId++;
                }
            });

        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    // ------------------ NEW: fetchOverallUsdiPayoutSolana ------------------
    const fetchOverallUsdiPayoutSolana = async () => {
        try {
            const response = await fetch(overallUsdiPayoutSolanaUrl);
            if (!response.ok) throw new Error('Failed to fetch overall_usdi_payout_solana');
            const payoutData = await response.json();
            if (payoutData && payoutData.length > 0) {
                let maxItem = payoutData[0];
                for (const item of payoutData) {
                    if (parseInt(item.distribution_id, 10) > parseInt(maxItem.distribution_id, 10)) {
                        maxItem = item;
                    }
                }
                setFinalDistributionAmountAfterManagementFees(
                    maxItem.final_distribution_amount_after_management_fees
                );
            }
        } catch (err) {
            console.error('Error fetching overall_usdi_payout_solana:', err);
        }
    };

    // ------------------ FetchUsdiInterestDistributionWalletPublicKey ------------------
    // 点击distribute的时候找到要给他转账的地址
    const fetchUsdiInterestDistributionWalletPublicKey = async () => {
        try {
            const response = await fetch(usdiInterestDistributionWalletPublicKeyUrl);
            if (!response.ok) throw new Error('Failed to fetch distribution wallet public key');
            const result = await response.json();
            if (result && result.USDi_Interest_Distribution_Wallet_Public_Key) {
                setUsdiInterestDistributionWalletPublicKey(result.USDi_Interest_Distribution_Wallet_Public_Key);
            }

        } catch (error) {
            console.error(
                'Error fetching usdi_interest_distribution_wallet_public_key:',
                error
            );
        }
    };

    // ------------------ fetchHistoricalData -> cum_dws table ------------------
    const fetchHistoricalData = async (address) => {
        try {
            const url = `${getUserBalanceEndpoint}?user_address=${address}`;
            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch historical data');
            const result = await response.json();

            setHistoricalData((prevData) => ({
                ...prevData,
                [address]: result.data,
            }));
        } catch (error) {
            console.error('Error fetching historical data:', error);
        }
    };

    // ------------------ 侧边表(cum_dws)的「下载CSV」函数 ------------------
    const handleDownloadSideTable = (address) => {
        const sideTable =
            historicalData[address]
                ?.slice()
                ?.sort((a, b) => b.block_number - a.block_number) || []; // Descending order

        if (!sideTable || sideTable.length === 0) {
            alert('No historical data to download for this address.');
            return;
        }

        const headers = [
            'Block Number',
            'Cum DWS',
            'Balance',
            'DWS',
            'BNP',
            'Undistributed USDI',
            'Cumulative Distributed USDI',
            'Distribution Transaction Hash',
            'kra_price_at_conversion',
            'referer_kra_bonus_in_equivelant',
            'cum_referers_kra_bonus'
        ];
        const csvRows = sideTable.map((item) => {
            const rowArr = [
                digitFormat(item.block_number),
                digitFormat(item.cum_dws),
                moneyFormat(item.balance),
                digitFormat(item.dws),
                digitFormat(item.bnp),
                item.undistributed_usdi !== null && item.undistributed_usdi !== undefined
                    ? moneyFormat4decimals(item.undistributed_usdi)
                    : '--',
                item.cum_distributed_usdi !== null && item.cum_distributed_usdi !== undefined
                    ? moneyFormat4decimals(item.cum_distributed_usdi)
                    : '--',
                item.distribution_transaction_hash && item.distribution_transaction_hash.trim() !== ''
                    ? item.distribution_transaction_hash
                    : '--',
                item.kra_price_at_conversion !== null && item.kra_price_at_conversion !== undefined && item.kra_price_at_conversion !== 0
                    ? moneyFormat4decimals(item.kra_price_at_conversion)
                    : '',
                item.referer_kra_bonus_in_equivelant !== null && item.referer_kra_bonus_in_equivelant !== undefined && item.referer_kra_bonus_in_equivelant !== 0
                    ? moneyFormat4decimals(item.referer_kra_bonus_in_equivelant)
                    : '',
                item.cum_referers_kra_bonus !== null && item.cum_referers_kra_bonus !== undefined && item.cum_referers_kra_bonus !== 0
                    ? moneyFormat4decimals(item.cum_referers_kra_bonus)
                    : ''
            ];
            return rowArr.map((field) => `"${field}"`).join(',');
        });
        const csvString = [headers.join(','), ...csvRows].join('\n');
        const blob = new Blob([csvString], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('download', `cum_dws_table_for_${address}.csv`);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ------------------ 挂载时或切换链时 ------------------
    useEffect(() => {
        fetchData();
        handleUpdate();
        fetchOverallUsdiPayoutSolana();
        fetchUsdiInterestDistributionWalletPublicKey();
        // eslint-disable-next-line
    }, [selectedChain]);

    // ------------------Admin-trigger ------------------
    const handleUpdate = async () => {
        setUpdating(true);               // 1) 在请求开始前设置 loading 状态
        try {
            const response = await fetch(adminTriggerEndpoint, {
                method: 'POST',
            });
            const result = await response;

            if (response.ok) {
                fetchData();
            } else {
                throw new Error('Failed to trigger distribution calculation');
            }
        } catch (err) {
            setError(err);
        } finally {
            setUpdating(false);
        }
    };

    // ------------------HandleUpdateDWS 要输入起始block number ------------------
    const handleUpdateDWS = async () => {
        let start_block = prompt('Enter Start Block Number:');
        let end_block = prompt('Enter End Block Number:');

        start_block = start_block ? start_block.replace(/,/g, '') : '';
        end_block = end_block ? end_block.replace(/,/g, '') : '';

        if (!start_block || !end_block) {
            alert('Both start block and end block numbers are required!');
            return;
        }

        try {
            setUpdatingDWS(true);
            const response = await fetch(userBalanceBlocksUpdateEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    start_block: start_block,
                    end_block: end_block,
                }),
            });

            if (response.ok) {
                alert('DWS updated successfully.');
            } else {
                const errorText = await response.text();
                throw new Error(`Error ${response.status}: ${errorText}`);
            }
        } catch (err) {
            setError(err);
            alert(`Error: ${err.message}`);
        } finally {
            setUpdatingDWS(false);
        }
    };

    // ------------------ HandleCalculate: Open modal, auto-populate from latest Distributed row (max distribution_id) in Overall USDI Payout ------------------
    const proceedToCalculateModal = async () => {
        setShowUndistributedConfirmModal(false);

        let prefillBlock = '';
        let prefillAmount = '';
        let prefillDistId = '';
        try {
            const response = await fetch(overallUsdiPayoutSolanaUrl);
            if (response.ok) {
                const payoutData = await response.json();
                if (payoutData && payoutData.length > 0) {
                    setPayoutListForCalculate(payoutData);
                    let latestDistributed = payoutData[0];
                    for (const item of payoutData) {
                        if (parseInt(item.distribution_id, 10) > parseInt(latestDistributed.distribution_id, 10)) {
                            latestDistributed = item;
                        }
                    }
                    prefillBlock = latestDistributed.block_no != null ? String(latestDistributed.block_no) : '';
                    prefillAmount = latestDistributed.final_distribution_amount_after_management_fees != null
                        ? String(latestDistributed.final_distribution_amount_after_management_fees) : '';
                    prefillDistId = latestDistributed.distribution_id != null ? String(latestDistributed.distribution_id) : '';
                } else {
                    setPayoutListForCalculate([]);
                    setLatestDistributionIdForDisplay('');
                }
            }
        } catch (err) {
            console.warn('Could not fetch payout data for auto-populate:', err);
            setPayoutListForCalculate([]);
            setLatestDistributionIdForDisplay('');
        }

        setLatestDistributionIdForDisplay(prefillDistId);
        setCalculateDistributionId(prefillDistId);
        setCalculateBlockNumber(prefillBlock);
        setCalculateFinalAmount(prefillAmount);
        setShowCalculateModal(true);
    };

    const handleUpdateByDistributionId = () => {
        const idStr = String(calculateDistributionId).trim();
        if (!idStr) return;
        const match = payoutListForCalculate.find(
            (item) => String(item.distribution_id).trim() === idStr
        );
        if (match) {
            setCalculateBlockNumber(match.block_no != null ? String(match.block_no) : '');
            setCalculateFinalAmount(match.final_distribution_amount_after_management_fees != null
                ? String(match.final_distribution_amount_after_management_fees) : '');
        } else {
            setCalculateBlockNumber('N/A');
            setCalculateFinalAmount('N/A');
        }
    };

    const handleCalculateClick = async () => {
        const hasUndistributed = data.some((row) => {
            const v = Number(row.user_undistributed_usdi);
            return Number.isFinite(v) && v !== 0;
        });
        if (hasUndistributed) {
            setShowUndistributedConfirmModal(true);
            return;
        }

        await proceedToCalculateModal();
    };

    const handleCalculateSubmit = async () => {
        const blockStr = String(calculateBlockNumber).trim();
        const amountStr = String(calculateFinalAmount).trim();
        if (blockStr === 'N/A' || amountStr === 'N/A') {
            alert('Block Number and Final Distribution Amount cannot be N/A. Enter a distribution ID that exists and click Update, or enter values manually.');
            return;
        }
        const block_number = parseInt(blockStr.replace(/,/g, ''), 10);
        const final_distribution_amount = parseFloat(amountStr.replace(/,/g, ''));

        if (!block_number || !Number.isFinite(block_number) || !final_distribution_amount || !Number.isFinite(final_distribution_amount)) {
            alert('Both Block Number and Final Distribution Amount are required and must be valid numbers!');
            return;
        }

        try {
            setCalculating(true);
            setShowCalculateModal(false);
            const response = await fetch(calculationTriggerEndpoint, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    block_number: Number(block_number),
                    final_distribution_amount: Number(final_distribution_amount),
                }),
            });
            if (response.ok) {
                fetchData();
            } else {
                throw new Error('Failed to calculate distribution');
            }
        } catch (err) {
            setError(err);
        } finally {
            setCalculating(false);
        }
    };

    // ------------------点击distribute button ------------------
    const handleClickInterestDistribute = async () => {
        const userHasSentFunds = window.confirm(
            "Have you already transferred the required USDi to the backend distribution wallet?\n\n" +
            "Click 'OK' if you have sent it.\nClick 'Cancel'  if you haven't sent it yet."
        );

        // 如果用户点击了“OK”(表示“我已经转了”)，则直接到下一步
        if (userHasSentFunds) {
            setIsTransferComplete(true)
            return;
        }

        await fetchOverallUsdiPayoutSolana()
        if (!finalDistributionAmountAfterManagementFees) {
            alert('Please distribute in overall USDI payout table first');
            return;
        }
        if (!usdiInterestDistributionWalletPublicKey) {
            alert('No distribution wallet public key found. Please try again');
            return;
        }
        if (usdiBalance < finalDistributionAmountAfterManagementFees) {
            const amountAsNumber = parseFloat(finalDistributionAmountAfterManagementFees);
            const truncatedValue = Math.floor(amountAsNumber * 1e6) / 1e6;

            const formattedValue = truncatedValue.toLocaleString('en-US', {
                maximumFractionDigits: 6
            });
            alert(
                `Insufficient funds or wallet not connected. Please try again. You need a balance greater than ${formattedValue.toLocaleString('en-US', {maximumFractionDigits: 6})}, current USDi balance ${usdiBalance}`
            );
            return;
        }
        const userConfirmed = window.confirm(
            `Are you sure you want to distribute ${finalDistributionAmountAfterManagementFees} USDI to the USDI Holders?`
        );
        if (userConfirmed) {
            setInterestDistributing(true);
            setAmount(finalDistributionAmountAfterManagementFees);
            setToAddress(usdiInterestDistributionWalletPublicKey);
            setTransactionTrigger((prev) => prev + 1);
            setInterestDistributing(false);
        }
    };

    // ------------------ 分页逻辑  ------------------
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const getPageNumbers = (curPage, totalPages) => {
        const maxPageButtons = 5;
        const pageNumbers = [];

        if (totalPages <= maxPageButtons) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);
            let left = curPage - 1;
            let right = curPage + 1;

            if (left > 2) {
                pageNumbers.push('...');
            } else {
                left = 2;
            }

            if (right < totalPages - 1) {
                for (let i = left; i <= right; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
            } else {
                for (let i = left; i <= totalPages - 1; i++) {
                    pageNumbers.push(i);
                }
            }
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };
    const handlePageChange = (page) => {
        if (page < 1) page = 1;
        if (page > totalPages) page = totalPages;
        setCurrentPage(page);
    };

    // ------------------ 下载整表CSV函数 ------------------
    const handleDownloadCSV = () => {
        const headers = [
            'User Unique ID',
            'User Class',
            'Address',
            'Current Block Number',
            'Last Block Number',
            'Status',
            'BNP',
            'Cum DWS',
            'Balance of USDI',
            'DWS',
            'New Cumulative DWS',
            'CumDWS Weight %',
            'Accrued Interest',
            'User Undistributed USDI',
            'Cumulative Distributed USDI',
            'New Cumulative DWS After Distribution'
        ];
        const csvRows = sortedData.map((row) => {
            return [
                row.userId !== Infinity ? row.userId : 'N/A',
                row.user_class,
                row.address,
                row.current_block_number,
                row.last_block_number,
                row.status,
                row.bnp,
                row.cum_dws,
                row.balance_of_usdi,
                row.dws,
                row.new_cum_dws,
                row.cum_dws_weight_percent,
                row.accrued_interest !== undefined
                    ? moneyFormat4decimals(row.accrued_interest)
                    : '',
                row.user_undistributed_usdi,
                row.cum_distributed_usdi,
                0.0
            ].map((field) => `"${field ?? ''}"`).join(',');
        });
        const csvString = [headers.join(','), ...csvRows].join('\n');
        const blob = new Blob([csvString], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'holder_info_solana.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ------------------ 排序逻辑  ------------------
    let sortedData = data.slice().sort((a, b) => {
        const aBal = Number(a.balance_of_usdi) || 0;
        const bBal = Number(b.balance_of_usdi) || 0;
        return bBal - aBal;
    });
    sortedData = sortedData.map((row, index) => ({
        ...row,
        userId: index + 1,
    }));
    const totalItems = sortedData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = sortedData.slice(startIndex, endIndex);

    // ------------------ NEW CODE START ------------------
    // 1) 用于 Modal 显示的 state
    const [showVerifyModal, setShowVerifyModal] = useState(false);          // 是否显示  让admin 确认地址的Modal
    const [verifyDistributionData, setVerifyDistributionData] = useState([]); // GET 回来的待分发数据
    const [distributionVerifyError, setDistributionVerifyError] = useState(null);
    const [confirmingDistribution, setConfirmingDistribution] = useState(false);
    const [distributionConfirmError, setDistributionConfirmError] = useState(null);
    const [distributionConfirmSuccess, setDistributionConfirmSuccess] = useState([]);
    // 正在distribute时的状态（相对本次 Confirm 快照，避免 status 接口在收尾后变空导致 0/0）
    const [distributionProgress, setDistributionProgress] = useState("0/0");
    const [startStatusCheck, setStartStatusCheck] = useState(false);
    const distributionSnapshotRef = useRef([]);
    const [deliveryHashByAddress, setDeliveryHashByAddress] = useState({});

    const DISTRIBUTION_CHUNK_SIZE = 10;

    const addrKey = (a) => (a || '').toLowerCase();

    const applyTxSignaturesToDeliveryMap = useCallback((txSignatures, snapshotRows) => {
        const sigs = Array.isArray(txSignatures) ? txSignatures : [];
        const snap = snapshotRows || distributionSnapshotRef.current;
        if (!snap.length) return;
        setDeliveryHashByAddress((prev) => {
            const next = {...prev};
            snap.forEach((item, idx) => {
                const k = addrKey(item.address);
                const ci = Math.floor(idx / DISTRIBUTION_CHUNK_SIZE);
                if (k && sigs[ci]) {
                    next[k] = String(sigs[ci]).split(',')[0].trim();
                }
            });
            const delivered = snap.filter((row) => {
                const h = next[addrKey(row.address)];
                return h && h.trim() !== '';
            }).length;
            setDistributionProgress(`${delivered}/${snap.length}`);
            return next;
        });
    }, [DISTRIBUTION_CHUNK_SIZE]);

    const fetchDistributionVerifyStatus = useCallback(async () => {
        try {
            const response = await fetch(interestDistributeVerifyStatusUrl);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to fetch distribution verify status: ${errorText}`);
            }
            const result = await response.json();
            const rows = (result && result.data) ? result.data : [];

            setDeliveryHashByAddress((prev) => {
                const next = {...prev};
                for (const item of rows) {
                    const key = addrKey(item.address);
                    const raw = (item.distribution_transaction_hash || '').trim();
                    if (!key || !raw) continue;
                    next[key] = raw.split(',')[0].trim();
                }
                const snap = distributionSnapshotRef.current;
                if (snap.length > 0) {
                    const delivered = snap.filter((row) => {
                        const h = next[addrKey(row.address)];
                        return h && h.trim() !== '';
                    }).length;
                    setDistributionProgress(`${delivered}/${snap.length}`);
                } else {
                    const total = rows.length;
                    const distributed = rows.filter(
                        (item) =>
                            item.distribution_transaction_hash &&
                            String(item.distribution_transaction_hash).trim() !== ''
                    ).length;
                    setDistributionProgress(`${distributed}/${total}`);
                }
                return next;
            });
        } catch (error) {
            console.error('Error fetching distribution verify status:', error);
        }
    }, [interestDistributeVerifyStatusUrl]);
    // 2) 当 分发完成后，自动获取待分发表
    useEffect(() => {
        if (isTransferComplete) {
            fetchDistributionVerifyData();
        }
    }, [isTransferComplete]);

    // 3) GET 获取待分发信息
    const fetchDistributionVerifyData = async () => {
        try {
            setDistributionVerifyError(null);
            const response = await fetch(`${Distribution_USDI}/interest_distribute_verify_info`);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to fetch verify info: ${errorText}`);
            }
            const result = await response.json();
            if (result && result.data) {
                setVerifyDistributionData(result.data);
                setShowVerifyModal(true); // 打开 Modal
            }
        } catch (error) {
            console.error('Error fetching distribution verify data:', error);
            setDistributionVerifyError(error.message);
            setShowVerifyModal(true); // 如果出错也显示 Modal
        }
    };

    // 4) 下载待分发信息表
    const handleDownloadVerifyCSV = () => {
        if (!verifyDistributionData || verifyDistributionData.length === 0) {
            alert("No distribution data to download.");
            return;
        }
        const headers = ["Address", "Amount"];
        const csvRows = verifyDistributionData.map(item =>
            [item.address, item.amount].map(field => `"${field}"`).join(',')
        );
        const csvString = [headers.join(','), ...csvRows].join('\n');
        const blob = new Blob([csvString], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "interest_distribute_verify_info.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 5) Confirm 分发 (POST)
    const handleConfirmDistribution = async () => {
        if (!verifyDistributionData || verifyDistributionData.length === 0) {
            alert("No distribution data to confirm. Please click the update button next to undistributed USDI to calculate first ");
            return;
        }
        const snapshot = verifyDistributionData.map((row) => ({...row}));
        distributionSnapshotRef.current = snapshot;
        setDeliveryHashByAddress({});
        setDistributionProgress(`0/${snapshot.length}`);

        setConfirmingDistribution(true);
        setDistributionConfirmError(null);
        setDistributionConfirmSuccess([]);
        setStartStatusCheck(true);
        let completedOk = false;
        try {
            const response = await fetch(`${Distribution_USDI}/interest_distribute_confirmed`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({data: verifyDistributionData})
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to confirm distribution: ${errorText}`);
            }
            const result = await response.json();
            if (result.success) {
                completedOk = true;
                if (Array.isArray(result.txSignatures)) {
                    setDistributionConfirmSuccess(result.txSignatures);
                    applyTxSignaturesToDeliveryMap(result.txSignatures, snapshot);
                } else {
                    setDistributionConfirmSuccess([JSON.stringify(result.txSignatures)]);
                }
                setStartStatusCheck(false);
                fetchData();
            }
            if (result.error) {
                setDistributionConfirmError(result.error);
            }
        } catch (err) {
            setDistributionConfirmError(err.message);
        } finally {
            setConfirmingDistribution(false);

            if (!completedOk) {
                setTimeout(() => {
                    setStartStatusCheck(false);
                }, 30000);
            }
        }
    };

    // ------------------ 查询分发状态（后端按每批最多 10 条写库，可轮询 interest_distribute_verify_status） ------------------
    useEffect(() => {
        let intervalId;
        if (startStatusCheck) {
            fetchDistributionVerifyStatus();
            intervalId = setInterval(() => {
                fetchDistributionVerifyStatus();
            }, 3000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [startStatusCheck, fetchDistributionVerifyStatus]);

    // 更新用户 member class
    const handleUserClassChange = async (walletAddress, selectedClass) => {
        try {
            const updatePayload = {
                user_class: selectedClass,
            };
            const url = `https://usdi-api-jlukzyz7wa-ew.a.run.app/update?table=usdi_holders_info_table_solana&key=address&value=${walletAddress}`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(updatePayload),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to update user class.');
            }
            fetchData()
        } catch (error) {
            console.error('Error updating user class:', error);
            alert(error.message);
        }
    };
    // ------------------ 渲染 ------------------
    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <>
            <div className="button_wrapper">
                <h3>USDI Holders Info</h3>
                <>{updateMessage}</>
                <div style={{display: "flex"}}>
                    <button
                        className={`button ${interestDistributing ? 'updating' : ''}`}
                        onClick={handleClickInterestDistribute}
                        disabled={interestDistributing}
                        style={{marginRight: '20px'}}
                    >
                        {interestDistributing ? 'Distributing' : 'Distribute'}
                    </button>
                    <button
                        className={`button ${updating ? 'updating' : ''}`}
                        onClick={handleUpdate}
                        disabled={updating}
                    >
                        {updating ? 'Updating' : 'Update'}
                    </button>
                </div>
            </div>

            <div className="table-responsive noto">
                <table className="table table-striped table-bordered mt-3">
                    <thead className="thead-dark">
                    <tr>
                        <th>User Unique ID</th>
                        <th>User Class</th>

                        <th>Address</th>
                        <th>Current Block Number</th>
                        <th>Last Block Number</th>
                        <th>Status</th>
                        <th>BNP</th>
                        <th>
                            Cum DWS
                            <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={updatingDWS}
                                onClick={handleUpdateDWS}
                                className="update-icon"
                            />
                        </th>
                        <th>Balance of USDI</th>
                        <th>DWS</th>
                        <th>New Cumulative DWS</th>
                        <th>CumDWS Weight %</th>
                        <th>Accrued Interest</th>
                        <th>
                            User Undistributed USDI
                            <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={calculating}
                                onClick={handleCalculateClick}
                                className="update-icon"
                            />
                        </th>
                        <th>Cumulative Distributed USDI</th>
                        <th>New Cumulative DWS After Distribution</th>
                    </tr>
                    </thead>

                    <tbody>
                    {currentItems.map((row, index) => (
                        <tr key={index}>
                            <td>{row.userId !== Infinity ? row.userId : 'N/A'}</td>
                            <td>
                                <select
                                    value={row.user_class}
                                    onChange={(e) => handleUserClassChange(row.address, e.target.value)}
                                    className="status_dropdown"
                                    style={{width: "130px"}}
                                >
                                    <option value="General Member">General Member</option>
                                    <option value="Silver VIP">Silver VIP</option>

                                    <option value="Gold VIP">Gold VIP</option>
                                    <option value="Sapphire VIP">Sapphire VIP</option>
                                </select>
                            </td>
                            <td>{row.address}</td>
                            <td>{digitFormat(row.current_block_number)}</td>
                            <td>{digitFormat(row.last_block_number)}</td>
                            <td>{row.status}</td>
                            <td>{digitFormat(row.bnp)}</td>

                            <td
                                onMouseEnter={() => {
                                    setHoveredAddress(row.address);
                                    if (!historicalData[row.address]) {
                                        fetchHistoricalData(row.address);
                                    }
                                }}
                                onMouseLeave={() => setHoveredAddress(null)}
                                style={{position: 'relative', cursor: 'pointer'}}
                            >
                                {digitFormat(row.cum_dws)}
                                {hoveredAddress === row.address && historicalData[row.address] && (
                                    <div className="popup">
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <strong>
                                                Cum Dws table for "{row.address}"
                                            </strong>
                                            <button
                                                style={{
                                                    width: '200px',
                                                    marginBottom: '20px',
                                                    marginRight: '20px',
                                                    marginTop: '20px',
                                                }}
                                                className="tableDownloadButton"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownloadSideTable(row.address);
                                                }}
                                            >
                                                Download CSV
                                            </button>
                                        </div>

                                        <table className="table table-sm">
                                            <thead>
                                            <tr>
                                                <th>Block Number</th>
                                                <th>Cum DWS</th>
                                                <th>Balance</th>
                                                <th>DWS</th>
                                                <th>BNP</th>
                                                <th>Undistributed USDI</th>
                                                <th>Cumulative Distributed USDI</th>
                                                <th>Distribution Transaction Hash</th>
                                                <th>kra_price_at_conversion</th>
                                                <th>referer_kra_bonus_in_equivelant</th>
                                                <th>cum_referers_kra_bonus</th>

                                            </tr>
                                            </thead>
                                            <tbody>
                                            {
                                                historicalData[row.address]
                                                    .slice()
                                                    .sort((a, b) => b.block_number - a.block_number) // Descending order
                                                    .map((histRow, idx) => (
                                                        <tr key={idx}>
                                                            <td>{digitFormat(histRow.block_number)}</td>
                                                            <td>{digitFormat(histRow.cum_dws)}</td>
                                                            <td>{moneyFormat(histRow.balance)}</td>
                                                            <td>{digitFormat(histRow.dws)}</td>
                                                            <td>{digitFormat(histRow.bnp)}</td>
                                                            <td>
                                                                {histRow.undistributed_usdi !== null &&
                                                                histRow.undistributed_usdi !== undefined
                                                                    ? moneyFormat4decimals(histRow.undistributed_usdi)
                                                                    : '--'}
                                                            </td>
                                                            <td>
                                                                {histRow.cum_distributed_usdi !== null &&
                                                                histRow.cum_distributed_usdi !== undefined
                                                                    ? moneyFormat4decimals(histRow.cum_distributed_usdi)
                                                                    : '--'}
                                                            </td>

                                                            <td>
                                                                {histRow.distribution_transaction_hash &&
                                                                histRow.distribution_transaction_hash.trim() !== ''
                                                                    ? histRow.distribution_transaction_hash
                                                                    : '--'}
                                                            </td>
                                                            <td>
                                                                {histRow.kra_price_at_conversion !== null &&
                                                                histRow.kra_price_at_conversion !== undefined && histRow.kra_price_at_conversion !== 0
                                                                    ? moneyFormat4decimals(histRow.kra_price_at_conversion)
                                                                    : '--'}
                                                            </td>
                                                            <td>
                                                                {histRow.referer_kra_bonus_in_equivelant !== null &&
                                                                histRow.referer_kra_bonus_in_equivelant !== undefined && histRow.referer_kra_bonus_in_equivelant !== 0
                                                                    ? moneyFormat4decimals(histRow.referer_kra_bonus_in_equivelant)
                                                                    : '--'}
                                                            </td>
                                                            <td>
                                                                {histRow.cum_referers_kra_bonus &&
                                                                histRow.cum_referers_kra_bonus.trim() !== '' && histRow.cum_referers_kra_bonus !== 0
                                                                    ? histRow.cum_referers_kra_bonus
                                                                    : '--'}
                                                            </td>
                                                        </tr>
                                                    ))
                                            }
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </td>

                            <td>{moneyFormat(row.balance_of_usdi)}</td>
                            <td>{digitFormat(row.dws)}</td>
                            <td>{digitFormat(row.new_cum_dws)}</td>
                            <td>{moneyFormat4decimals(row.cum_dws_weight_percent)}%</td>
                            <td>
                                {row.accrued_interest !== undefined
                                    ? moneyFormat4decimals(row.accrued_interest)
                                    : '--'}
                            </td>
                            <td>{moneyFormat4decimals(row.user_undistributed_usdi)}</td>
                            <td>{moneyFormat4decimals(row.cum_distributed_usdi)}</td>
                            <td>0.00</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="pagination-outer-container">
                    <div className="pagination-container" style={{marginTop: '10px'}}>
                        <button
                            className="pagination-button"
                            id="first-page-button"
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                        >
                            First
                        </button>
                        <button
                            className="pagination-button"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>

                        {getPageNumbers(currentPage, totalPages).map((p, i) =>
                            p === '...' ? (
                                <span key={i} className="pagination-ellipsis">
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={i}
                                    className={`pagination-button ${
                                        p === currentPage ? 'active-page' : ''
                                    }`}
                                    onClick={() => handlePageChange(p)}
                                >
                                    {p}
                                </button>
                            )
                        )}

                        <button
                            className="pagination-button"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                        <button
                            className="pagination-button"
                            id="last-page-button"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                        >
                            Last
                        </button>
                    </div>
                    <button className="tableDownloadButton" onClick={handleDownloadCSV}>
                        Download CSV
                    </button>
                </div>
            )}

            {/*
              --- Modal 用来展示验证分发信息 ---
            */}
            {showVerifyModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div style={{display: 'flex', justifyContent: "space-between"}}>
                            <h4>
                                Distribution Verify Info ({distributionProgress})
                            </h4>

                            <span style={{width: "30px", height: '30px', cursor: "pointer"}}
                                  onClick={() => {
                                      setShowVerifyModal(false);
                                      setIsTransferComplete(false);
                                      distributionSnapshotRef.current = [];
                                      setDeliveryHashByAddress({});
                                      setDistributionProgress('0/0');
                                      setStartStatusCheck(false);
                                  }}>
                            x
                        </span>
                        </div>
                        <div style={{marginTop: '20px'}}>
                            <button
                                className="button"
                                onClick={handleDownloadVerifyCSV}
                            >
                                Download CSV
                            </button>
                            <button
                                className="button"
                                onClick={handleConfirmDistribution}
                                disabled={confirmingDistribution}
                                style={{marginLeft: '10px'}}
                            >
                                {confirmingDistribution ? 'Confirming...' : 'Confirm'}
                            </button>

                        </div>

                        {distributionVerifyError && (
                            <div style={{color: 'red', marginBottom: '10px'}}>
                                Error fetching verify info: {distributionVerifyError}
                            </div>
                        )}
                        {/* 显示Confirm结果 */}
                        {distributionConfirmError && (
                            <div style={{marginTop: '10px', color: 'red'}}>
                                {distributionConfirmError}
                            </div>
                        )}
                        {distributionConfirmSuccess && distributionConfirmSuccess.length > 0 && (
                            <div style={{marginTop: '10px', color: 'green'}}>
                                <div>Distribution Success! Transaction Signatures:</div>
                                <ul>
                                    {distributionConfirmSuccess.map((sig, i) => (
                                        <li key={i}>{sig}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {verifyDistributionData && verifyDistributionData.length > 0 && (startStatusCheck || confirmingDistribution || Object.keys(deliveryHashByAddress).length > 0) && (
                            <div style={{marginTop: '12px', marginBottom: '8px', fontSize: '13px', color: '#aaa', lineHeight: 1.5}}>
                                <div style={{marginBottom: '6px'}}>
                                    On-chain progress (batches of {DISTRIBUTION_CHUNK_SIZE} match the backend). Addresses below show a signature once that row&apos;s batch has landed.
                                </div>
                                {(() => {
                                    const snap = distributionSnapshotRef.current.length
                                        ? distributionSnapshotRef.current
                                        : verifyDistributionData;
                                    const batches = [];
                                    for (let i = 0; i < snap.length; i += DISTRIBUTION_CHUNK_SIZE) {
                                        const slice = snap.slice(i, i + DISTRIBUTION_CHUNK_SIZE);
                                        const n = slice.filter((s) => {
                                            const h = deliveryHashByAddress[addrKey(s.address)];
                                            return h && String(h).trim() !== '';
                                        }).length;
                                        batches.push({
                                            label: Math.floor(i / DISTRIBUTION_CHUNK_SIZE) + 1,
                                            done: n,
                                            total: slice.length,
                                        });
                                    }
                                    return (
                                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                                            {batches.map((b) => (
                                                <span
                                                    key={b.label}
                                                    style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        background: b.done >= b.total ? 'rgba(46, 160, 67, 0.25)' : 'rgba(120, 120, 120, 0.2)',
                                                        border: `1px solid ${b.done >= b.total ? '#3fb950' : '#555'}`,
                                                    }}
                                                >
                                                    Batch {b.label}: {b.done}/{b.total}
                                                </span>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {verifyDistributionData && verifyDistributionData.length > 0 && (
                            <table className="table table-striped table-bordered mt-3">
                                <thead>
                                <tr>
                                    <th>Address</th>
                                    <th>Amount</th>
                                    <th>Delivery</th>
                                    <th>Tx (first sig)</th>
                                </tr>
                                </thead>
                                <tbody>
                                {verifyDistributionData.map((item, idx) => {
                                    const sig = deliveryHashByAddress[addrKey(item.address)] || '';
                                    const delivered = sig.trim() !== '';
                                    return (
                                        <tr key={idx}>
                                            <td>{item.address}</td>
                                            <td>{item.amount}</td>
                                            <td>
                                                {delivered
                                                    ? 'On-chain recorded'
                                                    : (confirmingDistribution || startStatusCheck ? 'Pending…' : '—')}
                                            </td>
                                            <td style={{fontSize: '11px', wordBreak: 'break-all'}}>
                                                {delivered ? (
                                                    <a
                                                        href={`https://solscan.io/tx/${sig}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {sig.length > 20 ? `${sig.slice(0, 12)}…${sig.slice(-8)}` : sig}
                                                    </a>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        )}


                    </div>
                </div>
            )}

            {/* Undistributed value confirmation modal - same style as Calculate modal */}
            {showUndistributedConfirmModal && (
                <div
                    className="modal-overlay"
                    style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999}}
                    onClick={(e) => e.target === e.currentTarget && setShowUndistributedConfirmModal(false)}
                >
                    <div className="modal-content" style={{maxWidth: '450px', padding: '24px'}} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{marginTop: 0, marginBottom: '16px', color: '#FFFFFFCC'}}>Undistributed Value Exists</h3>
                        <p style={{fontSize: '13px', color: '#aaa', marginBottom: '20px', lineHeight: 1.5}}>
                            There's undistributed value from previous rounds. Do you want to handle that (e.g. run payout) before continuing?
                        </p>
                        <p style={{fontSize: '12px', color: '#888', marginBottom: '20px', lineHeight: 1.5}}>
                            By running this you will overwrite existing undistributed value.<br />
                            Click "Cancel" to run payout first, then recalculate.
                        </p>
                        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                            <button
                                className="button"
                                onClick={() => setShowUndistributedConfirmModal(false)}
                                style={{backgroundColor: '#2B2B33', color: '#757576'}}
                            >
                                Cancel
                            </button>
                            <button className="button" onClick={proceedToCalculateModal}>
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Calculate modal - auto-populated from Overall USDI Payout (Calculated row), user can edit */}
            {showCalculateModal && (
                <div
                    className="modal-overlay"
                    style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999}}
                    onClick={(e) => e.target === e.currentTarget && setShowCalculateModal(false)}
                >
                    <div className="modal-content" style={{maxWidth: '450px', padding: '24px'}} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{marginTop: 0, marginBottom: '16px', color: '#FFFFFFCC'}}>Calculate User Undistributed USDI</h3>
                        <p style={{fontSize: '13px', color: '#aaa', marginBottom: '16px'}}>
                            Values are pre-filled from the latest Distributed row in Overall USDI Payout. Enter another distribution ID and click Update to load that row, or edit manually.
                        </p>
                        {latestDistributionIdForDisplay && (
                            <p style={{fontSize: '12px', color: '#888', marginBottom: '12px'}}>
                                Latest distribution ID is {latestDistributionIdForDisplay}.
                            </p>
                        )}
                        <div style={{marginBottom: '16px'}}>
                            <label style={{display: 'block', marginBottom: '6px', color: '#FFFFFFCC'}}>Distribution ID</label>
                            <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                                <input
                                    type="text"
                                    value={calculateDistributionId}
                                    onChange={(e) => setCalculateDistributionId(e.target.value)}
                                    placeholder="Distribution ID"
                                    style={{flex: 1, padding: '8px 12px', color: '#FFFFFFCC', backgroundColor: '#071112CC', border: '2px solid #1F464D', borderRadius: '5px'}}
                                />
                                <button type="button" className="button" onClick={handleUpdateByDistributionId} style={{flexShrink: 0, padding: '6px 12px', fontSize: '12px', width: 'auto', minWidth: 'auto', height: 'auto'}}>
                                    Update
                                </button>
                            </div>
                        </div>
                        <div style={{marginBottom: '16px'}}>
                            <label style={{display: 'block', marginBottom: '6px', color: '#FFFFFFCC'}}>Block Number</label>
                            <input
                                type="text"
                                value={calculateBlockNumber}
                                onChange={(e) => setCalculateBlockNumber(e.target.value)}
                                placeholder="Enter block number"
                                style={{width: '100%', padding: '8px 12px', color: '#FFFFFFCC', backgroundColor: '#071112CC', border: '2px solid #1F464D', borderRadius: '5px'}}
                            />
                        </div>
                        <div style={{marginBottom: '20px'}}>
                            <label style={{display: 'block', marginBottom: '6px', color: '#FFFFFFCC'}}>Final Distribution Amount</label>
                            <input
                                type="text"
                                value={calculateFinalAmount}
                                onChange={(e) => setCalculateFinalAmount(e.target.value)}
                                placeholder="Enter final distribution amount"
                                style={{width: '100%', padding: '8px 12px', color: '#FFFFFFCC', backgroundColor: '#071112CC', border: '2px solid #1F464D', borderRadius: '5px'}}
                            />
                        </div>
                        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                            <button
                                className="button"
                                onClick={() => setShowCalculateModal(false)}
                                style={{backgroundColor: '#2B2B33', color: '#757576'}}
                            >
                                Cancel
                            </button>
                            <button className="button" onClick={handleCalculateSubmit}>
                                Calculate
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HolderInfoTableSolana;